/**
 * Sends an email invitation to join the client as a member.
 *
 * @param clientId - Client ID used only for cache invalidation
 *
 * @returns Mutation result with `{ mutate, mutateAsync, isPending, isError, isSuccess }`
 *
 * @remarks
 * Invalidates `CLIENT_MEMBERS_KEYS.byClient(orgId, clientId)` on success.
 *
 * @example
 * const invite = useInviteClientMember('client-1');
 * await invite.mutateAsync({ email: 'new@company.com', role: 'client' });
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { clientInvitationsService } from '../api/clientMembers.service';
import { CLIENT_MEMBERS_KEYS } from './queryKeys';
import type { CreateInvitationPayload } from '../types';

export function useInviteClientMember(clientId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (payload: CreateInvitationPayload) =>
      clientInvitationsService.create(clientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_MEMBERS_KEYS.byClient(orgId, clientId) });
    },
  });
}
